import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useValidationRulesStore } from '@/store/validationRulesStore';
import { useTableDefinitionsStore } from '@/store/tableDefinitionsStore';
import type { TableEntry } from '@/api/endpoints/tableDefinitions';
import { useFooterStore } from '@/stores/footerStore';
import { useDataViewAuditFilterStore } from '@/stores/dataViewAuditFilterStore';
import { useUserPrefFormatting } from '@/composables/useUserPrefFormatting';
import { extractErrorMessage, getProblemFromAxiosError } from '@/helpers/errorHandlingHelpers';
import { buildQueryColumnsFromDataViewRun, findDataViewContractViolations, tableHasAddableNewRowColumns } from '@/helpers/dataViewColumnDisplay';
import { buildRunDataViewRequest } from '@/helpers/dataViewRunRequest';
import { buildFreeTextFromTerm } from '@/helpers/dataViewFreeTextSearch';
import {
  getColumnDisplayMetadata,
  getDataViewById,
  resolveDataViewIdByPublicId,
  runDataView,
  runDataViewRow,
  updateDataViewColumnDisplayPreferences,
  updateMyDataViewColumnDisplayPreferences
} from '@/api/endpoints/dataViews';
import { getDataModelById } from '@/api/endpoints/dataModels';
import type { QueryColumn, QueryFilterItem, QueryOrderByItem, QueryResult } from '@/api/contracts/queryResult';
import type {
  DataViewColumn,
  DataViewColumnDisplayItem,
  DataViewColumnDisplayPreferenceEntry,
  DataViewResponse,
  DataViewRunResponse,
  RowSelection,
  UpdateDataViewColumnDisplayPreferencesRequest
} from '@/api/contracts/dataModel';

export interface DataViewColumnDisplayCache {
  items: DataViewColumnDisplayItem[];
  preferences: DataViewColumnDisplayPreferenceEntry[];
}

export interface AvailableViewItem {
  viewId: string;
  publicId: string;
  name: string;
  isDefault?: boolean;
  routeName?: 'data-view-public' | 'table-public' | 'report-public';
}

export function getRowId (row: Record<string, unknown>): number | null {
  const id = row['icsRowId_'] ?? row['ICSRowId_'];
  if (id == null) return null;
  const n = Number(id);
  return Number.isNaN(n) ? null : n;
}

const ATTACHMENT_INDICATOR_FIELD = '_attachment_indicator';

// WI 2200: relasjonskolonner som backend har flagget som ugyldige (verdi utenfor koblet liste).
// columnId (= QueryColumn.field) leveres i radens metadata; markeres på lik linje med value-list-feil.
function relationInvalidFieldsForRow (row: Record<string, unknown>): string[] {
  const metadata = row._metadata as { invalidRelationColumns?: string[] | null } | undefined;
  const invalid = metadata?.invalidRelationColumns;
  return Array.isArray(invalid) ? invalid : [];
}

function buildCellErrorMapForRow (
  row: Record<string, unknown>,
  columns: QueryColumn[]
): Map<string, true> | null {
  const validationStore = useValidationRulesStore();
  let cellMap: Map<string, true> | null = null;
  for (const column of columns) {
    if (column.field === ATTACHMENT_INDICATOR_FIELD) continue;
    const ruleColumnName =
      column.sourceColumnName?.trim()
      || column.columnName?.trim()
      || column.columnKey?.trim()
      || column.field;
    const value = row[column.field];
    if (validationStore.isValidValueForColumn(ruleColumnName, value) === false) {
      cellMap ??= new Map<string, true>();
      cellMap.set(column.field, true);
    }
  }
  for (const invalidField of relationInvalidFieldsForRow(row)) {
    cellMap ??= new Map<string, true>();
    cellMap.set(invalidField, true);
  }
  return cellMap;
}

function buildValidationByRowId (
  rows: Record<string, unknown>[],
  columns: QueryColumn[]
): Map<number, Map<string, true>> {
  const out = new Map<number, Map<string, true>>();
  for (const row of rows) {
    const rowId = getRowId(row);
    if (rowId == null) continue;
    const cellMap = buildCellErrorMapForRow(row, columns);
    if (cellMap) out.set(rowId, cellMap);
  }
  return out;
}

function isGuidLike (value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

interface DataViewErrorSignals {
  hasLegacySignal: boolean;
  hasMaterializationDriftSignal: boolean;
  hasOrphanSignal: boolean;
  hasAccessSignal: boolean;
}

// Classify the backend detail string into the known runDataView error signals.
// hasAccessSignal folds the string-typed access check (detail contains tilgang)
// so the caller only needs to add the status-403 disjunction.
function detectErrorSignals (detail: unknown): DataViewErrorSignals {
  const isString = typeof detail === 'string';
  const d = isString ? detail.toLowerCase() : '';
  return {
    hasLegacySignal: isString && (detail.includes('oppdateres') || detail.includes('tableKey')),
    hasMaterializationDriftSignal: isString && (
      detail.includes('Regenerer materialisering')
      || detail.includes('materialiserte view-objektet for datamodellen er utdatert')
    ),
    hasOrphanSignal: d.includes('orphan')
      || d.includes('utdaterte koblinger')
      || d.includes('ikke lenger finnes i datamodellen')
      || d.includes('ugyldig queryplan'),
    hasAccessSignal: d.includes('tilgang')
  };
}

/**
 * Bug 3508: leser serverens `retryable`-flagg — svaret på om et nytt forsøk er kjent nytteløst.
 * Flagget mangler på enhver feil som ikke har tatt stilling, og da beholder flaten det den
 * allerede tilbød. Kun en feil som VET tar bort forsøket igjen.
 */
function dataViewRunErrorRetryable (err: unknown): boolean | null {
  const ax = err as { response?: { data?: { retryable?: unknown } } };
  const flag = ax?.response?.data?.retryable;
  return typeof flag === 'boolean' ? flag : null;
}

// Oversetter kjente backend-feilsignaler fra runDataView til brukerrettede meldinger.
function dataViewRunErrorMessage (err: unknown): string {
  const ax = err as { response?: { status?: number; data?: { title?: string; detail?: string; errors?: unknown } } };
  const status = ax?.response?.status;
  const detail = ax?.response?.data?.detail ?? '';
  const title = ax?.response?.data?.title ?? '';
  const errorsField = ax?.response?.data?.errors;
  const errors: string[] = Array.isArray(errorsField)
    ? errorsField.filter((e): e is string => typeof e === 'string')
    : [];

  // Bug 3508: bærer svaret en eksplisitt `retryable`-dom, har backend skrevet meldingen med vilje —
  // da konsumeres den ordrett, og gjettingen under skal ikke røre den. Uten dette leser
  // signal-sniffingen enkeltord ut av en ferdigskrevet tekst: tidsavbruddsmeldingen sier «har du
  // ikke tilgang til oppsettet», og hasAccessSignal gjorde den om til «du har ikke tilgang til
  // datavisningen». Sniffingen finnes for eldre svar som ikke sier hva de er.
  if (dataViewRunErrorRetryable(err) !== null && (title || detail)) {
    return [title, detail].filter(Boolean).join(': ');
  }

  const { hasLegacySignal, hasMaterializationDriftSignal, hasOrphanSignal, hasAccessSignal } = detectErrorSignals(detail);

  if (hasLegacySignal) {
    return 'Datamodellen er i gammelt format og må oppdateres. Be en administrator eller eier av modellen om å åpne modellen og bruke «Oppdater modell».';
  }
  if (hasMaterializationDriftSignal) {
    const lines = ['Datavisningen kan ikke lastes — det materialiserte view-objektet er utdatert.'];
    if (errors.length > 0) {
      lines.push('Detaljer:');
      errors.forEach((e) => lines.push(` • ${e}`));
    }
    lines.push('Åpne datamodellen og trykk «Regenerer materialisering» for å oppdatere view-en.');
    return lines.join('\n');
  }
  if (hasOrphanSignal) {
    if (errors.length > 0) {
      return [
        'Datavisningen inneholder utdaterte koblinger etter endringer i datamodellen.',
        'Detaljer:',
        ...errors.map((e) => ` • ${e}`)
      ].join('\n');
    }
    return 'Datavisningen inneholder utdaterte koblinger etter endringer i datamodellen. Noen data kan derfor ikke vises akkurat nå.';
  }
  if (status === 403 || hasAccessSignal) {
    return 'Du har ikke tilgang til å vise denne datavisningen. Be en administrator om å gi deg tilgang.';
  }
  if (status === 404) {
    return 'Datavisningen eller tilhørende modell ble ikke funnet. Den kan være slettet eller flyttet.';
  }
  if (errors.length > 0) {
    return [title || 'Forespørselen feilet', ...errors.map((e) => ` • ${e}`)].filter(Boolean).join('\n');
  }
  if (title || detail) {
    return [title, detail].filter(Boolean).join(': ');
  }
  return 'Kunne ikke laste datavisning. Prøv igjen senere eller kontakt support hvis feilen vedvarer.';
}

// Feilfangst for lagring av visningsalternativer eies her (ikke i komponenten):
// ved feil varsler storen brukeren (addProblem/addError) og signaliserer fiasko
// til kalleren via retur-verdien, slik at UI-laget bare styrer dialog-tilstand.
function notifySavePreferencesFailure (e: unknown): void {
  const notifications = useNotificationsStore();
  const problem = getProblemFromAxiosError(e);
  if (problem) {
    notifications.addProblem(problem);
  } else {
    notifications.addError('Feil', 'Kunne ikke lagre visningsalternativer');
  }
  console.error('[DataView preferences] kunne ikke lagre visningsalternativer', e);
}

async function saveMyColumnDisplayPreferences (
  viewId: string,
  request: UpdateDataViewColumnDisplayPreferencesRequest,
  signal?: AbortSignal
): Promise<boolean> {
  try {
    await updateMyDataViewColumnDisplayPreferences(viewId, request, signal);
    return true;
  } catch (e: unknown) {
    notifySavePreferencesFailure(e);
    return false;
  }
}

/** Bug 3535: server page size for one slice of the sliding window. */
const PAGE_SIZE = 100;
/** Bug 3535: how many server pages the window holds at once, capping rows in memory at 200. */
const MAX_LOADED_PAGES = 2;

export const useQueryStore = defineStore('dataViewQuery', () => {
  const columns = ref<QueryColumn[]>([]);
  const rows = ref<Record<string, unknown>[]>([]);
  const total = ref(0);
  const loading = ref(false);
  const error = ref<string | null>(null);
  // Bug 3508: null betyr «ingen har tatt stilling», ikke «kan prøves igjen».
  const errorRetryable = ref<boolean | null>(null);
  // US 3536: the selection is a DESCRIPTION, not a list of rows. Mode 'all' means every row the
  // query matches minus excludedRowIds, mode 'none' means only includedRowIds. That is what lets
  // "select all" cover fifty thousand rows while the sliding window from Bug 3535 holds at most two
  // hundred of them: the unbounded side is carried by the mode, and the id sets only ever hold what
  // the user ticked or unticked by hand.
  const selectionMode = ref<'none' | 'all'>('none');
  const includedRowIds = ref<Set<number>>(new Set());
  const excludedRowIds = ref<Set<number>>(new Set());
  // The row a range selection extends from. Held explicitly rather than read off the end of the
  // selection, which is in window order rather than in the order the user clicked.
  const selectionAnchorRowId = ref<number | null>(null);
  // US 3536: a described selection is expressed in row keys, so a row that has none cannot be part
  // of one. Such rows exist - an aggregate view need not project the row key - and they were
  // selectable before, so they are held as objects. They can never belong to a selection larger
  // than the window either, because a row you cannot name is a row no mass operation can reach.
  const selectedRowsWithoutId = ref<Record<string, unknown>[]>([]);
  // US 3536: how many rows the backend says are selected. Null means no answer is current, which is
  // the case between a click and the next page fetch, and the count is then worked out from the
  // selection total the backend already gave us.
  const backendSelectedCount = ref<number | null>(null);
  // US 3536: how many rows the view holds before the filters this run added.
  const totalUnfiltered = ref(0);
  const validationByRowId = ref<Map<number, Map<string, true>>>(new Map());

  // Bug 3535: the sliding window over server pages. firstLoadedPage is the one-based server page
  // number of rows[0], so the absolute index of a loaded row is derivable without keeping the
  // pages that were dropped. windowRequestId discards a response whose window is already stale.
  const firstLoadedPage = ref(1);
  const loadedPageCount = ref(0);
  const loadingWindow = ref(false);
  // What the window runs against: a view id in data view mode, a table key in table mode.
  const windowSourceId = ref<string | null>(null);
  let windowRequestId = 0;

  const rowsAbove = computed(() => (firstLoadedPage.value - 1) * PAGE_SIZE);
  const rowsBelow = computed(() => Math.max(0, total.value - rowsAbove.value - rows.value.length));
  const hasMoreAbove = computed(() => rowsAbove.value > 0);
  const hasMoreBelow = computed(() => rowsBelow.value > 0);

  /**
   * US 3536: whether one row is part of the selection, worked out from the description rather than
   * from object identity. Reading it by id is what makes a marked row survive its page being
   * dropped from the window and fetched again as a new object.
   */
  function isRowIdSelected (rowId: number): boolean {
    return selectionMode.value === 'all'
      ? !excludedRowIds.value.has(rowId)
      : includedRowIds.value.has(rowId);
  }

  /**
   * The selected rows the window actually holds. It is derived rather than stored, so a selection
   * larger than the window is expressed by the description and never has to be materialized. The
   * elements are the very row objects in rows, so a caller can still read and write their fields.
   */
  const selectedRows = computed<Record<string, unknown>[]>(() =>
    rows.value.filter((row) => {
      const id = getRowId(row);
      return id == null
        ? selectedRowsWithoutId.value.includes(row)
        : isRowIdSelected(id);
    })
  );

  const selectedRowIds = computed<Set<number>>(() => {
    const s = new Set<number>();
    for (const row of rows.value) {
      const id = getRowId(row);
      if (id != null && isRowIdSelected(id)) s.add(id);
    }
    return s;
  });

  function hasValidationErrorForCell (rowId: number | null, columnField: string): boolean {
    if (rowId == null) return false;
    return validationByRowId.value.get(rowId)?.get(columnField) === true;
  }

  /**
   * WI 3533: true when the column can carry an invalid value at all, so the filter offers the
   * option only where it can match something. The two sources are the SAME ones
   * buildCellErrorMapForRow uses to paint the cell red - a relation to another table, or a rule
   * about allowed values. Were they to drift apart, a user would get an option that never hits,
   * or miss the option on a column that is in fact red.
   */
  function canHaveValidationError (columnField: string): boolean {
    const column = columns.value.find((c) => c.field === columnField);
    if (!column) return false;
    if (column.relationLookupEnabled === true) return true;

    const validationStore = useValidationRulesStore();
    const ruleColumnName =
      column.sourceColumnName?.trim()
      || column.columnName?.trim()
      || column.columnKey?.trim()
      || column.field;
    return validationStore.getAllowedValuesForColumn(ruleColumnName) !== null;
  }

  const filters = ref<QueryFilterItem[]>([]);
  const orderBy = ref<QueryOrderByItem[]>([]);
  const freeTextTerm = ref<string>('');

  const currentView = ref<DataViewResponse | null>(null);
  const availableViews = ref<AvailableViewItem[]>([]);
  const mainTableKey = ref<string | null>(null);
  /**
   * Bug 2122: sant når hovedtabellen har minst én redigerbar ny-rad-kolonne. Settes pålitelig fra
   * den awaited tabell-definisjons-hentingen (ikke via publicId-cache-oppslag), slik at
   * "Legg til rad"-knappen virker i både datavisning og tabell-modus, uavhengig av om
   * `mainTableKey` er identisk med tabellens publicId.
   */
  const mainTableHasAddableColumns = ref(false);
  /** Datamodellens hovedtabell-alias (alias), for klassifisering av kolonner i datavisning. */
  const mainAlias = ref<string | null>(null);

  const columnDisplayByViewId = ref<Map<string, DataViewColumnDisplayCache>>(new Map());

  let _refreshCallback: (() => Promise<void>) | null = null;

  function getColumnDisplayForView (viewId: string): DataViewColumnDisplayCache | undefined {
    return columnDisplayByViewId.value.get(viewId);
  }

  function setColumnDisplayForView (viewId: string, cache: DataViewColumnDisplayCache): void {
    const next = new Map(columnDisplayByViewId.value);
    next.set(viewId, cache);
    columnDisplayByViewId.value = next;
  }

  function clearColumnDisplayForView (viewId: string): void {
    if (!columnDisplayByViewId.value.has(viewId)) return;
    const next = new Map(columnDisplayByViewId.value);
    next.delete(viewId);
    columnDisplayByViewId.value = next;
  }

  // ── data loading ────────────────────────────────────────────────────────────

  function load (result: QueryResult): void {
    columns.value = result.columns;
    rows.value = result.rows;
    total.value = result.total;
    // Bug 3535: a fresh result set restarts the window at the first server page.
    firstLoadedPage.value = 1;
    loadedPageCount.value = result.rows.length > 0 ? Math.ceil(result.rows.length / PAGE_SIZE) : 0;
    // US 3536: a fresh result set is a new selection. Changing the filter therefore starts the
    // marking over, which is what keeps a selection that is described rather than stored honest.
    clearSelection();
    totalUnfiltered.value = result.total;
    validationByRowId.value = buildValidationByRowId(result.rows, result.columns);
    filters.value.forEach((f) => {
      const col = columns.value.find((c) => c.field === f.columnName);
      if (col) { col.filterOperator = f.operator; col.filterValue = f.value; }
    });
    orderBy.value.forEach((o) => {
      const col = columns.value.find((c) => c.field === o.columnName);
      if (col) col.sortOrder = o.direction;
    });
    notifyDataViewContractViolations(result.columns);
  }

  // WI #1279: surface backend-kontraktsbrudd som eksplisitt feilmelding.
  // Hvis en kolonne er flagget skrivbar (isReadOnly=false) uten å levere
  // columnKey, ville save tidligere stille feile med 400 etter at FE-en
  // fallback'et til SQL-aliasen. Nå viser vi en konkret melding.
  function notifyDataViewContractViolations (cols: QueryColumn[]): void {
    const violations = findDataViewContractViolations(cols);
    if (violations.length === 0) return;
    const notifications = useNotificationsStore();
    for (const v of violations) {
      notifications.addError(
        'Datavisning: ugyldig kontrakt',
        `Kolonne "${v.label}" er flagget som skrivbar uten kontrakts-nøkkel fra backend. Endringer kan ikke lagres. Kontakt support.`
      );
      console.error('[DataView contract] writable column without columnKey', v);
    }
  }

  function setLoading (value: boolean): void {
    loading.value = value;
  }

  function setError (message: string): void {
    error.value = message;
    errorRetryable.value = null;
  }

  // Bug 3508: kjøre-feil bærer serverens dom over om et nytt forsøk hjelper, så meldingen og
  // dommen settes i samme steg — ellers kan flaten vise ny tekst med forrige feils handlingsvalg.
  function setRunError (err: unknown): void {
    error.value = dataViewRunErrorMessage(err);
    errorRetryable.value = dataViewRunErrorRetryable(err);
  }

  function clearError (): void {
    error.value = null;
    errorRetryable.value = null;
  }

  // ── refresh callback ─────────────────────────────────────────────────────────

  function setRefreshCallback (cb: () => Promise<void>): void {
    _refreshCallback = cb;
  }

  async function refreshQuery (): Promise<void> {
    if (!_refreshCallback) return;
    clearError();
    try {
      await _refreshCallback();
    } catch (e: unknown) {
      const notifications = useNotificationsStore();
      const problem = getProblemFromAxiosError(e);
      const msg = extractErrorMessage(e);
      error.value = msg;
      errorRetryable.value = dataViewRunErrorRetryable(e);
      if (problem) {
        notifications.addProblem(problem);
      } else {
        notifications.addError('Kunne ikke laste datavisning', msg);
      }
    }
  }

  function reselectRowById (rowId: number): void {
    const row = rows.value.find((r) => getRowId(r) === rowId);
    if (row != null) selectSingleRow(row);
    else clearSelection();
  }

  async function fetchRowAndReplace (viewId: string, rowId: number): Promise<void> {
    try {
      const result = await runDataViewRow(viewId, rowId);
      if (!result.columns?.length || !result.items?.length) {
        reselectRowById(rowId);
        return;
      }
      const freshRecord: Record<string, unknown> = {};
      result.columns.forEach((col, idx) => {
        freshRecord[col.columnId] = (result.items[0] as unknown[])[idx];
      });
      const metaByRowId = result.rowMetadataByRowId;
      if (metaByRowId?.[String(rowId)]) {
        freshRecord._metadata = metaByRowId[String(rowId)];
      }
      const idx = rows.value.findIndex((r) => getRowId(r) === rowId);
      if (idx >= 0) {
        rows.value.splice(idx, 1, freshRecord);
        const cellMap = buildCellErrorMapForRow(freshRecord, columns.value);
        const next = new Map(validationByRowId.value);
        if (cellMap) next.set(rowId, cellMap);
        else next.delete(rowId);
        validationByRowId.value = next;
      }
      reselectRowById(rowId);
    } catch (e: unknown) {
      const notifications = useNotificationsStore();
      const problem = getProblemFromAxiosError(e);
      const msg = extractErrorMessage(e);
      if (problem) {
        notifications.addProblem(problem);
      } else {
        notifications.addError('Kunne ikke hente rad på nytt', msg);
      }
      reselectRowById(rowId);
    }
  }

  // ── view-/query-lasting (flyttet fra DataViewDataView; feilfangst eies her) ──

  /**
   * Sett hovedtabell-nøkkel og hent tabelldefinisjonen. Avleder `mainTableHasAddableColumns` fra
   * retur-verdien (ikke fra et publicId-cache-oppslag), slik at "Legg til rad"-knappen aktiveres
   * pålitelig så snart hentingen er ferdig. Kallere venter ikke på dette (knappen oppdateres reaktivt).
   */
  function setMainTableKeyAndPrime (key: string | null): void {
    setMainTableKey(key);
    mainTableHasAddableColumns.value = false;
    const trimmed = String(key ?? '').trim();
    if (!trimmed) return;
    const tableDefinitionsStore = useTableDefinitionsStore();
    void tableDefinitionsStore.fetchTable(trimmed, { silent: true }).then((table: TableEntry | undefined) => {
      // Ignorer utdaterte svar dersom hovedtabellen er byttet i mellomtiden.
      if (mainTableKey.value === trimmed) {
        mainTableHasAddableColumns.value = tableHasAddableNewRowColumns(table);
      }
    });
  }

  /**
   * Slå opp viewId (GUID direkte eller via publicId) og hent datavisningen.
   * Feil fanges her og gir { viewId: '', view: null } — hosten viser «ikke funnet».
   */
  async function resolveAndFetchView (routeId: string): Promise<{ viewId: string; view: DataViewResponse | null }> {
    try {
      const resolved = isGuidLike(routeId) ? routeId : await resolveDataViewIdByPublicId(routeId);
      if (!resolved) {
        return { viewId: '', view: null };
      }
      const view = await getDataViewById(resolved);
      return { viewId: resolved, view };
    } catch {
      return { viewId: '', view: null };
    }
  }

  /**
   * Hent datamodellen for gjeldende view og sett mainAlias + hovedtabell-nøkkel.
   * Feil fanges her og nullstiller kontekst (uendret fallback-adferd fra hosten).
   */
  async function loadModelContext (modelId: string): Promise<void> {
    try {
      const m = await getDataModelById(modelId);
      setMainAlias(m.mainAlias ?? null);
      const alias = String(m.mainAlias || '').trim();
      const mainTable = (m.tables || []).find((table) => String(table.alias || '').trim() === alias);
      const resolvedKey = String(mainTable?.tableKey || '').trim() || null;
      setMainTableKeyAndPrime(resolvedKey);
    } catch {
      setMainAlias(null);
      setMainTableKeyAndPrime(null);
    }
  }

  /**
   * Builds the run request for the current filters, sorting and free text, for one window page.
   * Shared by the initial run and by every window move so both ask for the same selection.
   */
  function buildCurrentRequest (page: number): ReturnType<typeof buildRunDataViewRequest> {
    const auditStore = useDataViewAuditFilterStore();
    return buildRunDataViewRequest({
      page,
      pageSize: PAGE_SIZE,
      filters: filters.value,
      orderBy: orderBy.value,
      freeText: buildFreeTextFromTerm(freeTextTerm.value),
      changes: auditStore.currentFilter,
      // US 3536: the selection travels with every page fetch, because the backend stores none of
      // it. That is also what makes a page the user has never scrolled to come back marked.
      selection: currentSelection()
    });
  }

  /**
   * Maps a columnar run response into row records, attaching row metadata by row id when present.
   * Shared by the initial run and by every window move so both produce identical row shapes.
   */
  function mapRunResultRows (result: DataViewRunResponse): Record<string, unknown>[] {
    const rowIdColumnIndex = result.columns.findIndex(
      (c: DataViewColumn) => c.columnId === 'ICSRowId_' || c.columnId === 'icsRowId_'
    );
    const metaByRowId = result.rowMetadataByRowId ?? null;
    return result.items.map((row: unknown[]) => {
      const record: Record<string, unknown> = {};
      result.columns.forEach((col: DataViewColumn, idx: number) => {
        record[col.columnId] = row[idx];
      });
      if (rowIdColumnIndex >= 0 && metaByRowId) {
        const raw = (row as unknown[])[rowIdColumnIndex];
        const rowId = raw != null ? Number(raw) : Number.NaN;
        if (!Number.isNaN(rowId)) {
          record._metadata = metaByRowId[String(rowId)] ?? {};
        }
      }
      return record;
    });
  }

  /**
   * Bug 3535: moves the sliding window so it covers the row the user is looking at.
   * The window is always the page holding firstVisibleIndex plus the next one, clipped to the last
   * page. That single rule covers scrolling down, scrolling up and dragging the scrollbar to an
   * unloaded part of the result. Pages already loaded are reused, the rest are dropped, so memory
   * stays bounded at MAX_LOADED_PAGES pages no matter how large the selection is.
   * Selection is deliberately left untouched, so a marked row survives scrolling past it.
   */
  async function ensureWindowCovers (firstVisibleIndex: number): Promise<void> {
    const id = windowSourceId.value;
    if (!id || loadingWindow.value || total.value <= 0) return;

    const lastPage = Math.max(1, Math.ceil(total.value / PAGE_SIZE));
    const targetFirst = Math.min(
      lastPage,
      Math.floor(Math.max(0, firstVisibleIndex) / PAGE_SIZE) + 1
    );
    const targetLast = Math.min(lastPage, targetFirst + MAX_LOADED_PAGES - 1);

    const loadedFirst = firstLoadedPage.value;
    const loadedLast = loadedFirst + loadedPageCount.value - 1;
    if (loadedPageCount.value > 0 && targetFirst === loadedFirst && targetLast === loadedLast) return;

    const wanted: number[] = [];
    for (let page = targetFirst; page <= targetLast; page++) wanted.push(page);

    // Slice the pages worth keeping out of the current window before anything is awaited.
    const reused = new Map<number, Record<string, unknown>[]>();
    if (loadedPageCount.value > 0) {
      for (const page of wanted) {
        if (page < loadedFirst || page > loadedLast) continue;
        const offset = (page - loadedFirst) * PAGE_SIZE;
        reused.set(page, rows.value.slice(offset, offset + PAGE_SIZE));
      }
    }
    const missing = wanted.filter((page) => !reused.has(page));
    if (missing.length === 0) return;

    const requestId = ++windowRequestId;
    loadingWindow.value = true;
    try {
      const fetched = await Promise.all(
        missing.map((page) => runDataView(id, buildCurrentRequest(page)))
      );
      // A newer move took over while this one was in flight - its result is the current one.
      if (requestId !== windowRequestId) return;

      const byPage = new Map<number, Record<string, unknown>[]>(reused);
      missing.forEach((page, index) => {
        byPage.set(page, mapRunResultRows(fetched[index]));
      });

      const lastFetched = fetched.at(-1);
      rows.value = wanted.flatMap((page) => byPage.get(page) ?? []);
      firstLoadedPage.value = targetFirst;
      loadedPageCount.value = wanted.length;
      if (lastFetched) total.value = lastFetched.total;
      // US 3536: the selection survives the window moving, and the backend's ruling on which of the
      // newly fetched rows it holds is applied here. That is what makes rows in an area the user has
      // never scrolled to show up marked.
      missing.forEach((page, index) => {
        applySelectionAnswer(fetched[index], byPage.get(page) ?? []);
      });
      validationByRowId.value = buildValidationByRowId(rows.value, columns.value);
    } catch (err: unknown) {
      setRunError(err);
    } finally {
      loadingWindow.value = false;
    }
  }

  /**
   * Kjør datavisnings-/tabell-spørringen med gjeldende filtre/sortering/fritekst.
   * Feil fanges her og settes som reaktiv feiltilstand (error) for hosten.
   */
  async function runQuery (options: { tableMode?: boolean; tableKey?: string | null } = {}): Promise<void> {
    const footerStore = useFooterStore();
    const validationRulesStore = useValidationRulesStore();
    // WI 2086: radtall i footeren følger brukerens tallformat-prefs.
    const { formatNumber } = useUserPrefFormatting();

    if (options.tableMode === true) {
      const tableKey = String(options.tableKey ?? '').trim();
      if (!tableKey) return;
      await validationRulesStore.setTableKey(tableKey);

      const request = buildCurrentRequest(1);

      setLoading(true);
      try {
        const result = await runDataView(tableKey, request);
        const queryColumns = buildQueryColumnsFromDataViewRun(result.columns, [], tableKey, null);
        const mappedRows: Record<string, unknown>[] = mapRunResultRows(result);
        windowSourceId.value = tableKey;
        load({
          columns: queryColumns,
          rows: mappedRows,
          total: result.total,
          page: result.page,
          pageSize: result.pageSize
        });
        setMainTableKeyAndPrime(String(result.tableKey || tableKey));
        applySelectionAnswer(result, mappedRows);
        footerStore.set(`Antall rader: ${formatNumber(result.total)}`);
      } catch (err: unknown) {
        setRunError(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    const id = currentView.value?.viewId;
    if (!id) return;

    const request = buildCurrentRequest(1);

    setLoading(true);
    try {
      const displayBundlePromise = (async () => {
        const cached = getColumnDisplayForView(id);
        if (cached) return cached;
        const d = await getColumnDisplayMetadata(id);
        const bundle = {
          items: d.columns,
          preferences: d.columnDisplayPreferences ?? []
        };
        setColumnDisplayForView(id, bundle);
        return bundle;
      })();

      const [displayBundle, result] = await Promise.all([
        displayBundlePromise,
        runDataView(id, request)
      ]);

      // US 2884: kjoere-responsen baerer allerede brukerens kolonneoppsett. Display-metadataen
      // hentes fortsatt fordi den beskriver felttype/komponentvalg/relasjonsoppslag, ikke oppsett.
      const queryColumns = buildQueryColumnsFromDataViewRun(
        result.columns,
        displayBundle.items,
        mainTableKey.value,
        mainAlias.value
      );

      const mappedRows: Record<string, unknown>[] = mapRunResultRows(result);

      const queryResult: QueryResult = {
        columns: queryColumns,
        rows: mappedRows,
        total: result.total,
        page: result.page,
        pageSize: result.pageSize,
        nextPageToken: result.nextPageToken
      };

      const tableKey = String(result.tableKey || '').trim() || null;
      setMainTableKeyAndPrime(tableKey);
      // Reglene må være lastet FØR load — valideringskartet bygges
      // imperativt der, med reglene som finnes i øyeblikket (bug 2077).
      await validationRulesStore.setTableKey(tableKey);
      windowSourceId.value = id;
      load(queryResult);
      applySelectionAnswer(result, mappedRows);
      footerStore.set(`Antall rader: ${formatNumber(result.total)}`);
    } catch (err: unknown) {
      setRunError(err);
    } finally {
      setLoading(false);
    }
  }

  // ── selection ────────────────────────────────────────────────────────────────

  const hasSelectedRows = computed(() => selectedCount.value > 0);

  /**
   * US 3536: how many rows are selected, over the WHOLE selection rather than over the rows that
   * happen to be loaded. The backend owns the number and hands it back with every page fetch. In
   * between, it is worked out from the selection total the backend already gave us minus the rows
   * the user unticked by hand, which is arithmetic on two numbers the backend produced.
   */
  const selectedCount = computed(() => {
    const withoutId = selectedRowsWithoutId.value.length;
    if (backendSelectedCount.value !== null) return backendSelectedCount.value + withoutId;
    if (selectionMode.value === 'all') {
      return Math.max(0, total.value - excludedRowIds.value.size) + withoutId;
    }
    return includedRowIds.value.size + withoutId;
  });

  /**
   * US 3536: whether every selected row is loaded, so a caller can tell when a breakdown of the
   * selection can be worked out from the row objects and when it cannot.
   */
  const selectionFullyLoaded = computed(() => selectedCount.value === selectedRows.value.length);

  const getSelectionIcon = computed(() => {
    if (selectedCount.value === 0) return ['fal', 'square'];
    // US 3536: measured against the SELECTION, not against the window. Comparing with the loaded
    // rows was what made a selection of two hundred out of fifty thousand look complete.
    if (total.value > 0 && selectedCount.value >= total.value) return ['fas', 'square-check'];
    return ['fas', 'square-minus'];
  });

  /**
   * Marks or unmarks one row by id. Under mode 'all' that is an entry in the unticked set, under
   * mode 'none' an entry in the ticked set - so a single click never has to build a full id list.
   */
  /** Marks or unmarks a row that carries no row key, which can only ever be one the window holds. */
  function setRowWithoutIdSelected (row: Record<string, unknown>, selected: boolean): void {
    const without = selectedRowsWithoutId.value.filter((r) => r !== row);
    selectedRowsWithoutId.value = selected ? [...without, row] : without;
    backendSelectedCount.value = null;
  }

  function setRowIdSelected (rowId: number, selected: boolean): void {
    if (selectionMode.value === 'all') {
      if (selected) excludedRowIds.value.delete(rowId);
      else excludedRowIds.value.add(rowId);
    } else if (selected) {
      includedRowIds.value.add(rowId);
    } else {
      includedRowIds.value.delete(rowId);
    }
    // The backend's count described the selection as it was before this click.
    backendSelectedCount.value = null;
  }

  function selectSingleRow (row: Record<string, unknown>): void {
    const rowId = getRowId(row);
    selectionMode.value = 'none';
    excludedRowIds.value = new Set();
    includedRowIds.value = rowId != null ? new Set([rowId]) : new Set();
    selectedRowsWithoutId.value = rowId == null ? [row] : [];
    selectionAnchorRowId.value = rowId;
    backendSelectedCount.value = null;
  }

  function toggleRowSelection (row: Record<string, unknown>): void {
    const rowId = getRowId(row);
    if (rowId == null) {
      setRowWithoutIdSelected(row, !selectedRowsWithoutId.value.includes(row));
      return;
    }
    setRowIdSelected(rowId, !isRowIdSelected(rowId));
    selectionAnchorRowId.value = rowId;
  }

  function addRowSelection (row: Record<string, unknown>): void {
    const rowId = getRowId(row);
    if (rowId == null) {
      setRowWithoutIdSelected(row, true);
      return;
    }
    setRowIdSelected(rowId, true);
    selectionAnchorRowId.value = rowId;
  }

  function addRangeSelection (row: Record<string, unknown>): void {
    const targetIdx = rows.value.indexOf(row);
    const anchorIdx = selectionAnchorRowId.value == null
      ? -1
      : rows.value.findIndex((r) => getRowId(r) === selectionAnchorRowId.value);

    if (selectedCount.value === 0 || anchorIdx < 0) {
      selectSingleRow(row);
      return;
    }
    if (targetIdx < 0) return;

    const from = Math.min(anchorIdx, targetIdx);
    const to = Math.max(anchorIdx, targetIdx);
    for (let i = from; i <= to; i++) {
      const rangeRow = rows.value[i];
      if (rangeRow == null) continue;
      const rowId = getRowId(rangeRow);
      if (rowId != null) setRowIdSelected(rowId, true);
      else setRowWithoutIdSelected(rangeRow, true);
    }
  }

  function clearSelection (): void {
    selectionMode.value = 'none';
    includedRowIds.value = new Set();
    excludedRowIds.value = new Set();
    selectedRowsWithoutId.value = [];
    selectionAnchorRowId.value = null;
    backendSelectedCount.value = null;
  }

  function handleBulkSelection (mode: string): void {
    if (mode === 'all') {
      // US 3536: this is the whole point of the change. Selecting all switches the DESCRIPTION to
      // "everything the query matches" instead of copying the loaded rows, so it covers the whole
      // selection however much of it is in memory.
      selectionMode.value = 'all';
      includedRowIds.value = new Set();
      excludedRowIds.value = new Set();
      selectionAnchorRowId.value = null;
      backendSelectedCount.value = null;
    } else if (mode === 'none' || mode === 'unselect') {
      clearSelection();
    } else if (mode === 'invert' || mode === 'inverted') {
      // Inverting is only meaningful over the rows in hand, so it produces an explicit selection of
      // the loaded rows that were not marked.
      const inverted = new Set<number>();
      const invertedWithoutId: Record<string, unknown>[] = [];
      for (const r of rows.value) {
        const rowId = getRowId(r);
        if (rowId == null) {
          if (!selectedRowsWithoutId.value.includes(r)) invertedWithoutId.push(r);
        } else if (!isRowIdSelected(rowId)) {
          inverted.add(rowId);
        }
      }
      selectionMode.value = 'none';
      excludedRowIds.value = new Set();
      includedRowIds.value = inverted;
      selectedRowsWithoutId.value = invertedWithoutId;
      selectionAnchorRowId.value = null;
      backendSelectedCount.value = null;
    }
  }

  /**
   * US 3536: the selection as it goes on the wire. Null when nothing is selected, so a plain run
   * asks for nothing and the answer reports nothing selected.
   */
  function currentSelection (): RowSelection | null {
    if (selectionMode.value === 'none' && includedRowIds.value.size === 0) return null;
    return selectionMode.value === 'all'
      ? { mode: 'all', excludedRowIds: [...excludedRowIds.value] }
      : { mode: 'none', includedRowIds: [...includedRowIds.value] };
  }

  /**
   * US 3536: takes the backend's answer about a page it just handed out. The counts are the
   * backend's to own, and markedRowIds is the backend's ruling on which rows of THAT page the
   * selection holds - it is applied back onto the description so the client can never drift into a
   * second opinion about what is marked.
   */
  function applySelectionAnswer (
    result: DataViewRunResponse,
    pageRows: Record<string, unknown>[]
  ): void {
    if (typeof result.totalUnfiltered === 'number') totalUnfiltered.value = result.totalUnfiltered;
    if (typeof result.selectedCount === 'number') backendSelectedCount.value = result.selectedCount;

    const marked = result.markedRowIds;
    if (marked == null) return;
    const markedIds = new Set(marked.map(Number));
    for (const row of pageRows) {
      const rowId = getRowId(row);
      if (rowId == null) continue;
      const selectedByServer = markedIds.has(rowId);
      if (selectedByServer !== isRowIdSelected(rowId)) setRowIdSelected(rowId, selectedByServer);
    }
    // Reconciling must not throw the backend's own count away.
    if (typeof result.selectedCount === 'number') backendSelectedCount.value = result.selectedCount;
  }

  // ── sort ─────────────────────────────────────────────────────────────────────

  function setSort (columnName: string, direction: 'ASC' | 'DESC'): void {
    const existing = orderBy.value.find((o) => o.columnName === columnName);
    if (existing) {
      existing.direction = direction;
    } else {
      orderBy.value.push({ columnName, direction });
    }
    const col = columns.value.find((c) => c.field === columnName);
    if (col) col.sortOrder = direction;
  }

  function clearSort (columnName?: string): void {
    if (columnName) {
      orderBy.value = orderBy.value.filter((o) => o.columnName !== columnName);
      const col = columns.value.find((c) => c.field === columnName);
      if (col) col.sortOrder = null;
    } else {
      orderBy.value = [];
      columns.value.forEach((c) => { c.sortOrder = null; });
    }
  }

  // ── filter ───────────────────────────────────────────────────────────────────

  function getFilter (columnName: string): QueryFilterItem[] {
    return filters.value.filter((f) => f.columnName === columnName);
  }

  function setFilter (item: QueryFilterItem): void {
    filters.value = filters.value.filter((f) => f.columnName !== item.columnName);
    filters.value.push(item);
    const col = columns.value.find((c) => c.field === item.columnName);
    if (col) {
      col.filterOperator = item.operator;
      col.filterValue = item.value;
    }
  }

  function clearFilter (columnName: string): void {
    filters.value = filters.value.filter((f) => f.columnName !== columnName);
    const col = columns.value.find((c) => c.field === columnName);
    if (col) {
      col.filterOperator = null;
      col.filterValue = undefined;
    }
  }

  /**
   * US 3534: replaces the whole filter set in one go, so several rows on the SAME column survive.
   * setFilter keys on column name and drops the earlier rows for that column - right for the column
   * header menu, which has one filter per column, and wrong for the filter grid, where several rows
   * on one column is the entire point of letting the user choose and/or between them.
   *
   * The header mirror follows the FIRST row for a column. One header cell cannot show two filters,
   * and the first is the one the user reads top down.
   */
  function replaceAllFilters (rows: QueryFilterItem[]): void {
    filters.value = [...rows];
    columns.value.forEach((c) => {
      const first = rows.find((r) => r.columnName === c.field);
      c.filterOperator = first ? first.operator : null;
      c.filterValue = first ? first.value : undefined;
    });
  }

  function clearAllFilters (): void {
    filters.value = [];
    columns.value.forEach((c) => {
      c.filterOperator = null;
      c.filterValue = undefined;
    });
  }

  function setFreeText (term: string): void {
    freeTextTerm.value = term.trim();
  }

  function clearFreeText (): void {
    freeTextTerm.value = '';
  }

  function setCurrentView (view: DataViewResponse | null): void {
    currentView.value = view;
  }

  function setAvailableViews (views: AvailableViewItem[]): void {
    availableViews.value = views;
  }

  function setMainTableKey (id: string | null): void {
    const normalized = String(id ?? '').trim();
    mainTableKey.value = normalized.length > 0 ? normalized : null;
  }

  function setMainAlias (id: string | null): void {
    mainAlias.value = id;
  }

  function reset (): void {
    columns.value = [];
    rows.value = [];
    total.value = 0;
    firstLoadedPage.value = 1;
    loadedPageCount.value = 0;
    loadingWindow.value = false;
    windowSourceId.value = null;
    loading.value = false;
    error.value = null;
    errorRetryable.value = null;
    clearSelection();
    totalUnfiltered.value = 0;
    validationByRowId.value = new Map();
    filters.value = [];
    orderBy.value = [];
    freeTextTerm.value = '';
    _refreshCallback = null;
    currentView.value = null;
    availableViews.value = [];
    mainTableKey.value = null;
    mainTableHasAddableColumns.value = false;
    mainAlias.value = null;
  }

  // ── column resize ────────────────────────────────────────────────────────────

  function resizeColumn (field: string, width: number): void {
    const col = columns.value.find((c) => c.field === field);
    if (col) col.width = width;
  }

  async function saveColumnDisplayPreferences (
    viewId: string,
    request: UpdateDataViewColumnDisplayPreferencesRequest,
    signal?: AbortSignal
  ): Promise<DataViewResponse | null> {
    try {
      return await updateDataViewColumnDisplayPreferences(viewId, request, signal);
    } catch (e: unknown) {
      notifySavePreferencesFailure(e);
      return null;
    }
  }

  return {
    columns,
    rows,
    total,
    loading,
    error,
    errorRetryable,
    selectedRows,
    selectedRowIds,
    // US 3536: the selection description and the numbers that go with it.
    selectionMode,
    totalUnfiltered,
    selectionFullyLoaded,
    currentSelection,
    isRowIdSelected,
    // US 3536: the run request a mass operation must be anchored to, so it hits what the grid shows.
    buildCurrentRunRequest: () => buildCurrentRequest(1),
    filters,
    orderBy,
    freeTextTerm,
    currentView,
    availableViews,
    hasSelectedRows,
    selectedCount,
    getSelectionIcon,
    getRowId,
    hasValidationErrorForCell,
    canHaveValidationError,
    load,
    setLoading,
    setError,
    clearError,
    setRefreshCallback,
    refreshQuery,
    reselectRowById,
    fetchRowAndReplace,
    setMainTableKeyAndPrime,
    resolveAndFetchView,
    loadModelContext,
    ensureWindowCovers,
    rowsAbove,
    rowsBelow,
    hasMoreAbove,
    hasMoreBelow,
    loadingWindow,

    runQuery,
    selectSingleRow,
    toggleRowSelection,
    addRowSelection,
    addRangeSelection,
    clearSelection,
    handleBulkSelection,
    setSort,
    clearSort,
    getFilter,
    setFilter,
    clearFilter,
    clearAllFilters,
    replaceAllFilters,
    setFreeText,
    clearFreeText,
    resizeColumn,
    setCurrentView,
    setAvailableViews,
    mainTableKey,
    mainTableHasAddableColumns,
    setMainTableKey,
    mainAlias,
    setMainAlias,
    getColumnDisplayForView,
    setColumnDisplayForView,
    clearColumnDisplayForView,
    saveColumnDisplayPreferences,
    saveMyColumnDisplayPreferences,
    reset,
  };
});
